import * as Popover from "@radix-ui/react-popover";
import { useId, useState, type ComponentPropsWithoutRef } from "react";
import {
  DayPicker,
  getDefaultClassNames,
  type ChevronProps,
  type DateRange as DayPickerDateRange,
  type DayButtonProps,
  type Matcher,
} from "react-day-picker";

import { focusRing } from "../../foundations/a11y";
import { Outline } from "../../icons";
import { cn } from "../../lib/cn";
import { Button } from "../Button";
import { useNearestDialogPortalContainer } from "../Popover";

export type DateRangeValue = {
  start?: string;
  end?: string;
};

export type DateRangePreset = {
  label: string;
  value: DateRangeValue;
};

export type DateRangeSelectorProps = {
  label: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  helperText?: string;
  error?: string;
  presets?: DateRangePreset[];
  clearLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  startName?: string;
  endName?: string;
  numberOfMonths?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"div">, "onChange" | "value">;

const displayDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function compactRange(value: DateRangeValue): DateRangeValue {
  const next: DateRangeValue = {};
  if (value.start) next.start = value.start;
  if (value.end) next.end = value.end;
  return next;
}

function rangesMatch(left: DateRangeValue, right: DateRangeValue): boolean {
  return (
    (left.start ?? "") === (right.start ?? "") &&
    (left.end ?? "") === (right.end ?? "")
  );
}

function parseIsoDate(value?: string): Date | undefined {
  if (!value) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
}

function toIsoDate(date?: Date): string | undefined {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value?: string): string {
  const date = parseIsoDate(value);
  return date ? displayDateFormatter.format(date) : "Select date";
}

function formatRangeForLabel(value: DateRangeValue, placeholder: string) {
  if (value.start && value.end) {
    return `${formatDisplayDate(value.start)} - ${formatDisplayDate(value.end)}`;
  }

  if (value.start) {
    return `${formatDisplayDate(value.start)} - Select end`;
  }

  if (value.end) {
    return `Select start - ${formatDisplayDate(value.end)}`;
  }

  return placeholder;
}

function toDayPickerRange(
  value: DateRangeValue,
): DayPickerDateRange | undefined {
  const from = parseIsoDate(value.start);
  const to = parseIsoDate(value.end);

  if (!from && !to) return undefined;
  return { from, to };
}

function toDateRangeValue(range?: DayPickerDateRange): DateRangeValue {
  return compactRange({
    start: toIsoDate(range?.from),
    end: toIsoDate(range?.to),
  });
}

function getDisabledMatchers(minDate?: Date, maxDate?: Date): Matcher[] {
  const matchers: Matcher[] = [];
  if (minDate) matchers.push({ before: minDate });
  if (maxDate) matchers.push({ after: maxDate });
  return matchers;
}

function compareCalendarDates(left: Date, right: Date): number {
  return (
    Date.UTC(left.getFullYear(), left.getMonth(), left.getDate()) -
    Date.UTC(right.getFullYear(), right.getMonth(), right.getDate())
  );
}

function isSameCalendarDate(left: Date, right: Date): boolean {
  return compareCalendarDates(left, right) === 0;
}

function usePopoverOpenState({
  open,
  defaultOpen,
  onOpenChange,
}: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    defaultOpen ?? false,
  );
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : uncontrolledOpen;

  function setOpen(nextOpen: boolean) {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  }

  return [currentOpen, setOpen] as const;
}

export function DateRangeSelector({
  label,
  value,
  onChange,
  helperText,
  error,
  presets = [],
  clearLabel = "Clear dates",
  placeholder = "Select dates",
  disabled = false,
  required = false,
  min,
  max,
  startName,
  endName,
  numberOfMonths = 2,
  open,
  defaultOpen,
  onOpenChange,
  className,
  id,
  ...rest
}: DateRangeSelectorProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const labelId = `${fieldId}-label`;
  const [fieldRef, portalContainer] =
    useNearestDialogPortalContainer<HTMLDivElement>();
  const messageId = error
    ? `${fieldId}-error`
    : helperText
      ? `${fieldId}-help`
      : undefined;
  const hasValue = Boolean(value.start || value.end);
  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);
  const selectedRange = toDayPickerRange(value);
  const rangeText = formatRangeForLabel(value, placeholder);
  const triggerLabel = `${label}: ${rangeText}`;
  const initialMonth = selectedRange?.from ?? selectedRange?.to ?? minDate;
  const [isOpen, setIsOpen] = usePopoverOpenState({
    open,
    defaultOpen,
    onOpenChange,
  });

  function handleSelect(range?: DayPickerDateRange) {
    if (
      !selectedRange?.from &&
      range?.from &&
      range.to &&
      isSameCalendarDate(range.from, range.to)
    ) {
      onChange(toDateRangeValue({ from: range.from, to: undefined }));
      return;
    }

    const nextValue = toDateRangeValue(range);

    onChange(nextValue);

    if (nextValue.start && nextValue.end) {
      setIsOpen(false);
    }
  }

  return (
    <div
      ref={fieldRef}
      id={fieldId}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={messageId}
      aria-disabled={disabled ? true : undefined}
      data-required={required ? true : undefined}
      className={cn("flex flex-col gap-1.5", className)}
      {...rest}
    >
      <div className="flex items-center gap-3">
        <span id={labelId} className="text-label text-text">
          {label}
        </span>
      </div>

      <div className="flex max-w-full items-center gap-2">
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={triggerLabel}
              aria-describedby={messageId}
              className={cn(
                "inline-flex h-10 w-full min-w-0 items-center justify-start gap-2 rounded-md border border-separator bg-surface px-3 py-2 text-left text-body text-text transition-colors duration-base ease-standard hover:bg-fill",
                "disabled:cursor-not-allowed disabled:opacity-50",
                focusRing,
                error && "ring-1 ring-inset ring-error",
              )}
            >
              <Outline.CalendarDaysIcon
                className="text-text-secondary size-5 shrink-0"
                aria-hidden="true"
              />
              <span className={cn("truncate", !hasValue && "text-placeholder")}>
                {rangeText}
              </span>
            </button>
          </Popover.Trigger>
          <Popover.Portal container={portalContainer ?? undefined}>
            <Popover.Content
              aria-label={`${label} calendar`}
              align="start"
              sideOffset={8}
              className={cn(
                "z-50 w-auto rounded-lg border border-separator bg-surface p-4 shadow-overlay outline-none",
                "data-[state=open]:animate-fadeIn",
              )}
            >
              <DateRangeCalendar
                selected={selectedRange}
                onSelect={handleSelect}
                defaultMonth={initialMonth}
                minDate={minDate}
                maxDate={maxDate}
                numberOfMonths={numberOfMonths}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {hasValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-label={clearLabel}
            onClick={() => onChange({})}
            className="text-text-tertiary hover:text-text size-8 shrink-0 px-0"
            leftIcon={
              <Outline.XMarkIcon className="size-4" aria-hidden="true" />
            }
          />
        )}
      </div>

      {startName ? (
        <input type="hidden" name={startName} value={value.start ?? ""} />
      ) : null}
      {endName ? (
        <input type="hidden" name={endName} value={value.end ?? ""} />
      ) : null}

      {presets.length > 0 && (
        <div
          role="group"
          className="flex flex-wrap gap-2"
          aria-label={`${label} presets`}
        >
          {presets.map((preset) => {
            const selected = rangesMatch(value, preset.value);
            return (
              <button
                key={preset.label}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => onChange(compactRange(preset.value))}
                className={cn(
                  "inline-flex h-8 items-center rounded-full px-3 text-label transition-colors duration-base ease-standard",
                  "disabled:pointer-events-none disabled:opacity-50",
                  selected
                    ? "bg-accent-wash text-accent"
                    : "bg-fill text-text-secondary hover:bg-fill-secondary hover:text-text",
                  focusRing,
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      )}

      {error ? (
        <p id={`${fieldId}-error`} className="text-caption text-error-text">
          {error}
        </p>
      ) : helperText ? (
        <p id={`${fieldId}-help`} className="text-caption text-text-secondary">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

type DateRangeCalendarProps = {
  selected?: DayPickerDateRange;
  onSelect: (range?: DayPickerDateRange) => void;
  defaultMonth?: Date;
  minDate?: Date;
  maxDate?: Date;
  numberOfMonths: number;
};

function DateRangeCalendar({
  selected,
  onSelect,
  defaultMonth,
  minDate,
  maxDate,
  numberOfMonths,
}: DateRangeCalendarProps) {
  const defaultClassNames = getDefaultClassNames();
  const disabledMatchers = getDisabledMatchers(minDate, maxDate);

  return (
    <DayPicker
      mode="range"
      selected={selected}
      onSelect={onSelect}
      defaultMonth={defaultMonth}
      startMonth={minDate}
      endMonth={maxDate}
      numberOfMonths={numberOfMonths}
      showOutsideDays
      pagedNavigation
      excludeDisabled
      disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
      className={cn("w-fit", defaultClassNames.root)}
      classNames={{
        months: cn(
          "relative flex flex-col gap-6 md:flex-row",
          defaultClassNames.months,
        ),
        month: cn("flex w-full flex-col gap-3", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex items-center justify-between gap-1",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          calendarNavButtonClassName,
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          calendarNavButtonClassName,
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "flex h-9 items-center justify-center px-10",
          defaultClassNames.month_caption,
        ),
        caption_label: cn(
          "text-label font-medium text-text",
          defaultClassNames.caption_label,
        ),
        month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "flex size-9 items-center justify-center text-caption font-normal text-text-secondary",
          defaultClassNames.weekday,
        ),
        week: cn("mt-1 flex w-full", defaultClassNames.week),
        day: cn("relative size-9 p-0 text-center", defaultClassNames.day),
        selected: cn(defaultClassNames.selected),
        range_start: cn(
          "rounded-l-md bg-accent-wash",
          defaultClassNames.range_start,
        ),
        range_middle: cn(
          "rounded-none bg-accent-wash",
          defaultClassNames.range_middle,
        ),
        range_end: cn(
          "rounded-r-md bg-accent-wash",
          defaultClassNames.range_end,
        ),
        today: cn(defaultClassNames.today),
        outside: cn("text-text-tertiary", defaultClassNames.outside),
        disabled: cn(
          "pointer-events-none text-text-tertiary opacity-40",
          defaultClassNames.disabled,
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
      }}
      components={{
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
      }}
    />
  );
}

const calendarNavButtonClassName = cn(
  "inline-flex size-8 items-center justify-center rounded-md text-text-secondary transition-colors duration-base ease-standard hover:bg-fill hover:text-text",
  "disabled:pointer-events-none disabled:opacity-50",
  focusRing,
);

function CalendarChevron({ className, orientation, ...props }: ChevronProps) {
  const Icon =
    orientation === "left"
      ? Outline.ChevronLeftIcon
      : orientation === "right"
        ? Outline.ChevronRightIcon
        : Outline.ChevronDownIcon;

  return (
    <Icon className={cn("size-4", className)} aria-hidden="true" {...props} />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps) {
  const selectedSingle =
    modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle;

  return (
    <button
      data-day={toIsoDate(day.date)}
      data-selected-single={selectedSingle || undefined}
      data-range-start={modifiers.range_start || undefined}
      data-range-end={modifiers.range_end || undefined}
      data-range-middle={modifiers.range_middle || undefined}
      className={cn(
        "flex size-9 items-center justify-center rounded-md text-label font-normal leading-none text-text transition-colors duration-base ease-standard hover:bg-fill-secondary",
        "disabled:pointer-events-none disabled:opacity-40",
        "data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-accent-wash data-[range-middle=true]:text-text data-[range-middle=true]:hover:bg-accent-wash",
        "data-[range-start=true]:bg-accent data-[range-start=true]:text-on-accent",
        "data-[range-end=true]:bg-accent data-[range-end=true]:text-on-accent",
        "data-[selected-single=true]:bg-accent data-[selected-single=true]:text-on-accent",
        modifiers.today && !modifiers.selected && "font-semibold text-accent",
        modifiers.outside && "text-text-tertiary",
        focusRing,
        className,
      )}
      {...props}
    />
  );
}
